import { off, on } from './event-utils';
import { getSupportedFeatures } from './get-supported-features';
import { grep } from './grep';
import { Observable } from './Observable';
import { applyEventMap, eventMap } from './scroller/event-map';
import { extend, now } from './scroller/utils';
const preventDefault = (e) => {
    e.preventDefault();
};
const noop = () => { };
const CLICK_DELAY = 300, PRESS = 'press', HOLD = 'hold', SELECT = 'select', START = 'start', MOVE = 'move', END = 'end', CANCEL = 'cancel', TAP = 'tap', DOUBLETAP = 'doubleTap', RELEASE = 'release', GESTURESTART = 'gesturestart', GESTURECHANGE = 'gesturechange', GESTUREEND = 'gestureend', GESTURETAP = 'gesturetap';
const THRESHOLD = {
    'api': 0,
    'touch': 0,
    'mouse': 3,
    'pointer': 3
};
let DEFAULT_MIN_HOLD = 800, DEFAULT_THRESHOLD = 0;
function touchDelta(touch1, touch2) {
    const x1 = touch1.x.location, y1 = touch1.y.location, x2 = touch2.x.location, y2 = touch2.y.location, dx = x1 - x2, dy = y1 - y2;
    return {
        center: {
            x: (x1 + x2) / 2,
            y: (y1 + y2) / 2
        },
        distance: Math.sqrt(dx * dx + dy * dy)
    };
}
function getTouches(e) {
    const touches = [], originalEvent = e.originalEvent || e, currentTarget = e.currentTarget;
    if (e.api) {
        touches.push({
            id: 2, // hardcoded ID for API call
            event: e,
            target: e.target,
            currentTarget: e.target,
            location: e,
            type: 'api'
        });
    }
    else {
        touches.push({
            location: originalEvent,
            event: e,
            target: e.target,
            currentTarget: currentTarget,
            id: originalEvent.pointerId,
            type: 'pointer'
        });
    }
    return touches;
}
class TouchAxis {
    constructor(axis, location) {
        this.support = getSupportedFeatures();
        this.invalidZeroEvents = this.support.mobileOS && this.support.mobileOS.android;
        this.axis = axis;
        this._updateLocationData(location);
        this.startLocation = this.location;
        this.velocity = this.delta = 0;
        this.timeStamp = now();
    }
    move(location) {
        const offset = location['page' + this.axis], timeStamp = now(), timeDelta = timeStamp - this.timeStamp || 1;
        if (!offset && this.invalidZeroEvents) {
            return;
        }
        this.delta = offset - this.location;
        this._updateLocationData(location);
        this.initialDelta = offset - this.startLocation;
        this.velocity = this.delta / timeDelta;
        this.timeStamp = timeStamp;
    }
    _updateLocationData(location) {
        const axis = this.axis;
        this.location = location['page' + axis];
        this.client = location['client' + axis];
        this.screen = location['screen' + axis];
    }
}
class Touch {
    constructor(userEvents, target, touchInfo) {
        extend(this, {
            x: new TouchAxis('X', touchInfo.location),
            y: new TouchAxis('Y', touchInfo.location),
            type: touchInfo.type,
            threshold: userEvents.threshold || THRESHOLD[touchInfo.type],
            userEvents: userEvents,
            target: target,
            currentTarget: touchInfo.currentTarget,
            initialTouch: touchInfo.target,
            id: touchInfo.id,
            pressEvent: touchInfo,
            _clicks: userEvents._clicks,
            supportDoubleTap: userEvents.supportDoubleTap,
            _moved: false,
            _finished: false
        });
    }
    press() {
        this._holdTimeout = setTimeout(() => this._hold(), this.userEvents.minHold);
        this._trigger(PRESS, this.pressEvent);
    }
    _tap(touchInfo) {
        this.userEvents._clicks++;
        if (this.userEvents._clicks === 1) {
            this._clickTimeout = setTimeout(() => {
                if (this.userEvents._clicks === 1) {
                    this._trigger(TAP, touchInfo);
                }
                else {
                    this._trigger(DOUBLETAP, touchInfo);
                }
                this.userEvents._clicks = 0;
            }, CLICK_DELAY);
        }
    }
    _hold() {
        this._trigger(HOLD, this.pressEvent);
    }
    move(touchInfo) {
        const preventMove = touchInfo.type !== 'api' && this.userEvents._shouldNotMove;
        if (this._finished || preventMove) {
            return;
        }
        this.x.move(touchInfo.location);
        this.y.move(touchInfo.location);
        if (!this._moved) {
            if (this._withinIgnoreThreshold()) {
                return;
            }
            if (!UserEvents.current || UserEvents.current === this.userEvents) {
                this._start(touchInfo);
            }
            else {
                return this.dispose();
            }
        }
        if (!this._finished) {
            this._trigger(MOVE, touchInfo);
        }
    }
    end(touchInfo) {
        this.endTime = now();
        if (this._finished) {
            return;
        }
        this._finished = true;
        this._trigger(RELEASE, touchInfo);
        if (this._moved) {
            this._trigger(END, touchInfo);
        }
        else {
            if (this.supportDoubleTap) {
                this._tap(touchInfo);
            }
            else {
                this._trigger(TAP, touchInfo);
            }
        }
        clearTimeout(this._holdTimeout);
        this.dispose();
    }
    dispose() {
        const userEvents = this.userEvents, activeTouches = userEvents.touches || [];
        this._finished = true;
        this.pressEvent = null;
        clearTimeout(this._holdTimeout);
        // activeTouches.splice($.inArray(this, activeTouches), 1);
        const activeTouchIndex = activeTouches.indexOf(this);
        activeTouches.splice(activeTouchIndex, 1);
    }
    skip() {
        this.dispose();
    }
    cancel() {
        this.dispose();
    }
    isMoved() {
        return this._moved;
    }
    _start(touchInfo) {
        clearTimeout(this._holdTimeout);
        this.startTime = now();
        this._moved = true;
        this._trigger(START, touchInfo);
    }
    _trigger(name, touchInfo) {
        const e = touchInfo.event;
        const data = {
            touch: this,
            x: this.x,
            y: this.y,
            target: this.target,
            event: e
        };
        if (this.userEvents.notify(name, data)) {
            e.preventDefault();
        }
    }
    _withinIgnoreThreshold() {
        const xDelta = this.x.initialDelta, yDelta = this.y.initialDelta;
        return Math.sqrt(xDelta * xDelta + yDelta * yDelta) <= this.threshold;
    }
}
function withEachUpEvent(callback) {
    const downEvents = eventMap.up.split(' '), length = downEvents.length;
    for (let idx = 0; idx < length; idx++) {
        callback(downEvents[idx]);
    }
}
export default class UserEvents extends Observable {
    constructor(element, options) {
        super();
        const support = getSupportedFeatures();
        this.support = support;
        options = options || {};
        this.options = options;
        const filter = this.filter = options.filter;
        this.threshold = options.threshold || DEFAULT_THRESHOLD;
        this.minHold = options.minHold || DEFAULT_MIN_HOLD;
        this.touches = [];
        this._maxTouches = options.multiTouch ? 2 : 1;
        this.allowSelection = options.allowSelection;
        this.captureUpIfMoved = options.captureUpIfMoved;
        this._clicks = 0;
        this.supportDoubleTap = options.supportDoubleTap;
        extend(this, {
            element: element,
            surface: options.surface || element,
            stopPropagation: options.stopPropagation,
            pressed: false
        });
        this._surfaceMoveHandler = this._move.bind(this);
        on(this.surface, applyEventMap('move'), this._surfaceMoveHandler);
        this._surfaceEndHandler = this._end.bind(this);
        on(this.surface, applyEventMap('up cancel'), this._surfaceEndHandler);
        this._elementStartHandler = this._start.bind(this);
        on(element, applyEventMap('down'), filter, this._elementStartHandler);
        element.style['touch-action'] = options.touchAction || 'none';
        if (options.preventDragEvent) {
            this._elementDragStartHandler = preventDefault;
            on(element, applyEventMap('dragstart'), this._elementDragStartHandler);
        }
        // element.on(kendo.applyEventMap('mousedown'), filter, {
        //     root: element
        // } '_select');
        // todo: use root
        this._elementSelectHandler = this._select.bind(this);
        on(element, applyEventMap('mousedown'), filter, this._elementSelectHandler);
        if (this.captureUpIfMoved) {
            const surfaceElement = this.surface;
            this.preventIfMovingProxy = this.preventIfMoving.bind(this);
            withEachUpEvent((eventName) => {
                surfaceElement.addEventListener(eventName, this.preventIfMovingProxy, true);
            });
        }
        this.bind([
            PRESS,
            HOLD,
            TAP,
            DOUBLETAP,
            START,
            MOVE,
            END,
            RELEASE,
            CANCEL,
            GESTURESTART,
            GESTURECHANGE,
            GESTUREEND,
            GESTURETAP,
            SELECT
        ], options);
    }
    preventIfMoving(e) {
        if (this._isMoved()) {
            e.preventDefault();
        }
    }
    destroy() {
        const options = this.options;
        const element = this.element;
        if (this._destroyed) {
            return;
        }
        this._destroyed = true;
        if (this.captureUpIfMoved) {
            const surfaceElement = this.surface;
            withEachUpEvent((eventName) => {
                surfaceElement.removeEventListener(eventName, this.preventIfMovingProxy, true);
            });
        }
        off(this.surface, applyEventMap('move'), this._surfaceMoveHandler);
        off(this.surface, applyEventMap('up cancel'), this._surfaceEndHandler);
        off(element, applyEventMap('down'), this._elementStartHandler);
        if (options.preventDragEvent) {
            off(element, applyEventMap('dragstart'), this._elementDragStartHandler);
        }
        off(element, applyEventMap('mousedown'), this._elementSelectHandler);
        this._disposeAll();
        this.unbind();
        delete this.surface;
        delete this.element;
        delete this.currentTarget;
    }
    capture() {
        UserEvents.current = this;
    }
    cancel() {
        this._disposeAll();
        this.trigger(CANCEL);
    }
    notify(event, data) {
        const touches = this.touches;
        let eventName = event;
        if (this._isMultiTouch()) {
            switch (eventName) {
                case MOVE:
                    eventName = GESTURECHANGE;
                    break;
                case END:
                    eventName = GESTUREEND;
                    break;
                case TAP:
                    eventName = GESTURETAP;
                    break;
                default:
                    break;
            }
            extend(data, {
                touches: touches
            }, touchDelta(touches[0], touches[1]));
        }
        return this.trigger(eventName, extend(data, {
            type: eventName
        }));
    }
    press(x, y, target) {
        this._apiCall('_start', x, y, target);
    }
    move(x, y) {
        this._apiCall('_move', x, y);
    }
    end(x, y) {
        this._apiCall('_end', x, y);
    }
    _isMultiTouch() {
        return this.touches.length > 1;
    }
    _maxTouchesReached() {
        return this.touches.length >= this._maxTouches;
    }
    _disposeAll() {
        const touches = this.touches;
        while (touches.length > 0) {
            touches.pop().dispose();
        }
    }
    _isMoved() {
        return grep(this.touches, function (touch) {
            return touch.isMoved();
        }).length;
    }
    _select(e) {
        if (!this.allowSelection || this.trigger(SELECT, { event: e })) {
            e.preventDefault();
        }
    }
    _start(e) {
        if (e.which && e.which > 1 || this._maxTouchesReached()) {
            return;
        }
        UserEvents.current = null;
        this.currentTarget = e.currentTarget;
        if (this.stopPropagation) {
            e.stopPropagation();
        }
        let target;
        const eventTouches = getTouches(e);
        for (let idx = 0; idx < eventTouches.length; idx++) {
            if (this._maxTouchesReached()) {
                break;
            }
            const eventTouch = eventTouches[idx];
            if (this.filter) {
                target = eventTouch.currentTarget;
            }
            else {
                target = this.element;
            }
            if (target && target.length === 0) {
                continue;
            }
            const touch = new Touch(this, target, eventTouch);
            this.touches.push(touch);
            touch.press();
            if (this._isMultiTouch()) {
                this.notify('gesturestart', {});
            }
        }
    }
    _move(e) {
        this._eachTouch('move', e);
    }
    _end(e) {
        this._eachTouch('end', e);
    }
    _eachTouch(methodName, e) {
        const dict = {}, touches = getTouches(e), activeTouches = this.touches;
        let idx, touch, touchInfo, matchingTouch;
        for (idx = 0; idx < activeTouches.length; idx++) {
            touch = activeTouches[idx];
            dict[touch.id] = touch;
        }
        for (idx = 0; idx < touches.length; idx++) {
            touchInfo = touches[idx];
            matchingTouch = dict[touchInfo.id];
            if (matchingTouch) {
                const shouldCapture = methodName === 'move' && touchInfo.type === 'pointer' && !this.surface.hasPointerCapture(touchInfo.id);
                if (shouldCapture) {
                    this.surface.setPointerCapture(touchInfo.id);
                }
                matchingTouch[methodName](touchInfo);
            }
        }
    }
    _apiCall(type, x, y, target) {
        this[type]({
            api: true,
            pageX: x,
            pageY: y,
            clientX: x,
            clientY: y,
            target: target || this.element,
            stopPropagation: noop,
            preventDefault: noop
        });
    }
    static defaultThreshold(value) {
        DEFAULT_THRESHOLD = value;
    }
    static minHold(value) {
        DEFAULT_MIN_HOLD = value;
    }
}
